// @ts-check
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { MetalColors } from '@theme/custom.metal-colors';

/** Characters a name may be engraved in. Case is left as typed: forcing Title Case costs
 *  "McDonald" and initials like "JJ". */
// Unsupported glyphs (hyphens, accents, digits on most name fonts) render as tofu, so keep
// `\p{L}` off until a font's coverage is verified.
const LETTERS_ONLY = /[^A-Za-z ]+/g;
// Fonts with extra glyphs opt in via the block's `data-charset`. Mirrors the block's `pattern` and
// CHARSET_DISALLOW in custom.customizer-control.js. A Map, so `constructor` misses the prototype.
/** @type {Map<string, RegExp>} */
const CHARSET_DISALLOW = new Map([
  ['letters', LETTERS_ONLY],
  ['alphanumeric', /[^A-Za-z0-9 ]+/g],
  ['numeric', /[^0-9]+/g],
  ['coordinates', /[^A-Za-z0-9 .°]+/g],
]);

/**
 * Reduces a typed value to what can be engraved, dropping anything the pattern disallows.
 *
 * @param {string} value
 * @param {RegExp} disallowed
 * @returns {string}
 */
function engravable(value, disallowed) {
  return value.replace(disallowed, '');
}

/**
 * @typedef {object} NameJewelryRefs
 * @property {HTMLElement} root - The block root carrying `data-metal-tint` / `data-product-id`.
 * @property {HTMLScriptElement} [metalColors] - The store's metal colors, as
 *   snippets/custom.customizer-metal-colors.liquid emits them.
 * @property {HTMLElement} [preview] - The live name preview, omitted without a configured font
 *   or with the block's preview switch off.
 * @property {HTMLInputElement} textInput - The name input.
 * @property {HTMLElement} characterCount - The character counter.
 * @property {HTMLInputElement} [configInput] - Hidden `properties[_config]` work order. Optional
 *   because the component works without it: the readable name property is what gets engraved.
 */

/**
 * Name-jewelry PDP input: mirrors the typed name into a live preview rendered in the
 * product's name font and tinted to the selected metal. Name posts as a line-item property.
 * @extends Component<NameJewelryRefs>
 */
class NameJewelry extends Component {
  requiredRefs = ['root', 'textInput', 'characterCount'];

  /** @type {MetalColors | null} */
  #metalColors = null;

  connectedCallback() {
    super.connectedCallback();
    // A value can arrive without ever firing `input` (session restore, bfcache, a prefill), so
    // enforce before the first mirror or disallowed characters ride through to the order.
    this.#enforceRules();
    this.#sync();
    // Not in the theme's `on:` vocabulary, so it needs a real listener. See handleInput.
    this.refs.textInput.addEventListener('compositionend', this.#onCompositionEnd);
    const closestSection = this.closest('.shopify-section, dialog');
    (closestSection ?? document).addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.textInput.removeEventListener('compositionend', this.#onCompositionEnd);
    const closestSection = this.closest('.shopify-section, dialog');
    (closestSection ?? document).removeEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate
    );
  }

  /** Re-sync after a section re-render, where refs are rebound. */
  updatedCallback() {
    this.#enforceRules();
    this.#sync();
  }

  /**
   * Re-mirror the input into the preview + counter.
   *
   * @param {Event} [event]
   */
  handleInput(event) {
    // Reassigning .value mid-composition garbles what an IME shopper is composing, and the allowed
    // set is ASCII letters or digits anyway, so leave it: #onCompositionEnd enforces once committed.
    if (event instanceof InputEvent && event.isComposing) {
      this.#sync();
      return;
    }
    this.#enforceRules();
    this.#sync();
  }

  /**
   * Tidies before the Enter key submits the form.
   *
   * Enter does not blur the field, so without this an untidied value posts. keydown runs
   * before the key's default action, which is the implicit submission, so the tidy lands
   * ahead of constraint validation rather than racing it. Chosen over relying on `change`
   * firing first, which is not something the spec pins down.
   *
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key !== 'Enter') return;
    this.handleBlur();
  }

  /** Tidies the finished name on blur: no leading/trailing space, no double space between words.
   *  Not on every keystroke: typing "Mary " then "J" would eat the space, giving "MaryJ". */
  handleBlur() {
    const { textInput } = this.refs;
    const tidied = textInput.value.trim().replace(/ {2,}/g, ' ');
    if (tidied === textInput.value) return;
    textInput.value = tidied;
    this.#sync();
  }

  /** Enforce once the IME hands the composed text over. */
  #onCompositionEnd = () => {
    this.#enforceRules();
    this.#sync();
  };

  /** Drops anything that cannot be engraved, keeping the caret in place. Live guard covering
   *  pastes/autofill; `maxlength`/`pattern` back it up at submit if the script never loads. */
  // Reassigning .value drops the field's native undo stack, so this only touches it when something
  // was rejected; ordinary typing returns early and Ctrl+Z keeps working.
  #enforceRules() {
    const { textInput } = this.refs;
    const disallowed = this.#disallowed();
    const typed = textInput.value;
    const allowed = engravable(typed, disallowed);
    if (allowed === typed) return;

    // Put the caret back where it was, minus whatever was dropped ahead of it. Without
    // this it snaps to the end of the field on every rejected keystroke mid-word.
    const caret = textInput.selectionStart ?? typed.length;
    const kept = engravable(typed.slice(0, caret), disallowed).length;
    textInput.value = allowed;
    textInput.setSelectionRange(kept, kept);
  }

  /** The disallowed-character pattern for this field, from the block's `data-charset`
   *  (an unknown or missing value is letters + spaces). */
  #disallowed() {
    return CHARSET_DISALLOW.get(this.refs.root.dataset.charset ?? '') ?? LETTERS_ONLY;
  }

  /** Click-to-focus proxy for the preview: it mirrors the input, so clicking it must act like
   *  clicking the input, focusing it with the caret at the end. */
  focusInput() {
    const { textInput } = this.refs;
    textInput.focus();
    const end = textInput.value.length;
    textInput.setSelectionRange(end, end);
  }

  #sync() {
    const { textInput, preview, characterCount } = this.refs;
    const value = textInput.value;

    if (preview && value.trim() === '') {
      // Monogram fonts overlap glyphs: even the empty sample must fit the product's cap.
      preview.textContent = textInput.placeholder.slice(0, textInput.maxLength);
      preview.classList.add('is-empty');
    } else if (preview) {
      preview.textContent = value;
      preview.classList.remove('is-empty');
    }

    const template = characterCount.getAttribute('data-template');
    if (template) {
      characterCount.textContent = template
        .replace('[current]', String(value.length))
        .replace('[max]', String(textInput.maxLength));
    }

    this.#syncConfig(value);
  }

  /**
   * Mirror the typed name into the hidden `properties[_config]` work order.
   *
   * An inline product carries `custom.customizer`, so the cart-checkout-validation function
   * (cart-transform-app/.../cart_validations_generate_run.rs) treats its lines as customizer
   * lines and rejects any that has no valid `_config`. The block renders a valid empty skeleton,
   * and this keeps `selections` in step so the order also records WHAT was typed, under the same
   * control key an overlay order would use.
   *
   * Rebuilt from the skeleton in the DOM each time rather than mutated in place, so a stale or
   * hand-edited value cannot accumulate. An empty name yields an empty `selections` - the same
   * thing cartConfig() does, which skips blank values.
   * @param {string} value - The current input value.
   */
  #syncConfig(value) {
    const { configInput } = this.refs;
    if (!configInput) return;

    const key = configInput.dataset.controlKey;
    /** @type {Record<string, unknown>} */
    let doc;
    try {
      doc = JSON.parse(configInput.defaultValue);
    } catch {
      // Server-rendered constant skeleton, so this cannot come from our markup. Bail rather than
      // invent one: the name property still ships the name, the attribute keeps the server's value.
      return;
    }
    doc.selections = value.trim() === '' || !key ? [] : [{ key, value }];
    configInput.value = JSON.stringify(doc);
  }

  /** @returns {MetalColors} */
  #readMetalColors() {
    if (this.#metalColors) return this.#metalColors;
    /** @type {{ metalColors?: import('@theme/custom.metal-colors').MetalColorRecord[] }} */
    let island = {};
    try {
      island = JSON.parse(this.refs.metalColors?.textContent || '{}');
    } catch {
      // An unreadable island groups nothing, so the tint stays where Liquid put it.
    }
    this.#metalColors = new MetalColors(island.metalColors);
    return this.#metalColors;
  }

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const detail =
      /** @type {{ resource?: { title?: string, options?: string[] }, data?: { productId?: string } }} */ (
        /** @type {CustomEvent} */ (event).detail
      );
    const productId = detail?.data?.productId;
    if (productId && productId !== this.refs.root.dataset.productId) return;

    const variant = detail?.resource;
    if (!variant) return;

    const colors = this.#readMetalColors();
    const tint = (variant.options ?? []).map((option) => colors.baseOf(option)).find(Boolean);
    // No grouped metal (a size/length-only change): keep the current tint rather than reset it.
    if (tint) this.refs.root.dataset.metalTint = tint;
  };
}

customElements.define('name-jewelry-component', NameJewelry);
